import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosConfig';
import { getApiError, getBlobApiError } from '../utils/apiError';
import { Modal } from './common';

function AssignTabletModal({ tablet, onClose, onAssign }) {
  const [empleados, setEmpleados] = useState([]);
  const [selectedEmpleado, setSelectedEmpleado] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [useManual, setUseManual] = useState(false);
  const [manualText, setManualText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEmpleados();
  }, []);

  const fetchEmpleados = async () => {
    try {
      const response = await axiosInstance.get('/api/empleados/sin-tablet');
      setEmpleados(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error al obtener empleados:', err);
      setError('Error al cargar la lista de empleados');
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!useManual && !selectedEmpleado) {
      setError('Seleccione un empleado o active Asignación Manual');
      return;
    }
    if (useManual && !manualText.trim()) {
      setError('Ingrese un texto para Asignación Manual');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmAssign = async (generatePDF) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = useManual
        ? { asignacion_manual: manualText.trim() }
        : { empleado_id: selectedEmpleado };
      await axiosInstance.post(`/api/tablets/${tablet.id}/asignar`, payload);

      if (generatePDF) {
        try {
          const response = await axiosInstance.get(
            `/api/tablets/${tablet.id}/constancia`,
            { responseType: 'blob' }
          );

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          const empleado = empleados.find(e => e.id === parseInt(selectedEmpleado));
          link.setAttribute('download', `constancia_tablet_${empleado?.nombre || 'asignacion_manual'}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error al generar el PDF:', error);
          toast.error(await getBlobApiError(error, 'La tablet quedó asignada, pero no se pudo generar la constancia'));
        }
      }

      onAssign();
    } catch (error) {
      console.error('Error al asignar tablet:', error);
      setError(getApiError(error, 'Error al asignar la tablet'));
      setShowConfirmModal(false);
      setSubmitting(false);
    }
  };

  if (showConfirmModal) {
    const empleado = empleados.find(e => e.id === parseInt(selectedEmpleado));
    return (
      <Modal isOpen={true} onClose={onClose} size="md">
        <Modal.Header onClose={onClose}>Confirmar Asignación</Modal.Header>
        <Modal.Body>
          <p>¿Desea generar la constancia de entrega?</p>
          <div className="asset-info">
            <p><strong>Marca:</strong> {tablet.marca}</p>
            <p><strong>Modelo:</strong> {tablet.modelo}</p>
            <p><strong>Empleado:</strong> {useManual ? manualText : empleado?.nombre}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={() => handleConfirmAssign(false)} className="btn btn-secondary" disabled={submitting}>
            No Generar
          </button>
          <button onClick={() => handleConfirmAssign(true)} className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Asignando...' : 'Generar PDF'}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal isOpen={!!tablet} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>Asignar Tablet</Modal.Header>
      <Modal.Body>
        <div className="asset-info">
          <p><strong>Marca:</strong> {tablet.marca}</p>
          <p><strong>Modelo:</strong> {tablet.modelo}</p>
          <p><strong>Serial:</strong> {tablet.serial}</p>
          <p><strong>IMEI:</strong> {tablet.imei}</p>
        </div>

        {error && <p className="error-message">{error}</p>}

        {loading ? (
          <p>Cargando empleados...</p>
        ) : (
          <div className="form-group">
            <div className="manual-assignment-row">
              <label>
                <input
                  type="checkbox"
                  checked={useManual}
                  onChange={(e) => {
                    setUseManual(e.target.checked);
                    setError(null);
                  }}
                />{' '}
                Asignación Manual
              </label>
            </div>
            {!useManual && (
              <>
                <label htmlFor="empleado">Seleccionar Empleado:</label>
                <select
                  id="empleado"
                  value={selectedEmpleado}
                  onChange={(e) => setSelectedEmpleado(e.target.value)}
                  className="form-control"
                >
                  <option value="">Seleccione un empleado</option>
                  {empleados.map((empleado) => (
                    <option key={empleado.id} value={empleado.id}>
                      {empleado.nombre} - {empleado.departamento}
                    </option>
                  ))}
                </select>
              </>
            )}
            {useManual && (
              <>
                <label htmlFor="asignacionManual">Texto de Asignación Manual:</label>
                <input
                  id="asignacionManual"
                  type="text"
                  value={manualText}
                  onChange={(e) => setManualText(e.target.value)}
                  className="form-control"
                  placeholder="Ej: Departamento de Contabilidad, Juan Pérez (asesor)"
                />
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
        <button
          onClick={handleAssign}
          className="btn btn-primary"
          disabled={loading || (!useManual && !selectedEmpleado) || (useManual && !manualText.trim())}
        >
          Asignar
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default AssignTabletModal;
